import React, { useState, useEffect } from 'react';
import { getApiUrl, API_ENDPOINTS } from '../config/apiConfig';
import { useParams, Link, useNavigate } from 'react-router-dom';
import styled, { css } from 'styled-components';
import { motion } from 'framer-motion';
import { 
  FaArrowLeft, FaArrowRight, FaHeart, FaRegHeart, FaBookmark, 
  FaRegBookmark, FaHome, FaClosedCaptioning, FaMicrophone,
  FaExclamationTriangle, FaTrash, FaUser, FaEye, FaEyeSlash
} from 'react-icons/fa';

import Header from '../components/Header';
import AdBanner from '../components/AdBanner';
import SEO from '../components/SEO';
import { getImageUrl as getImageUrlHelper } from '../utils/imageHelper';

// --- Styled Components ---
const WatchPageContainer = styled(motion.div)`
  padding: 100px 5% 50px 5%;
  min-height: 100vh;
  background: var(--bg-color);
`;

const ShowTitleLink = styled(Link)`
  font-size: 1.2rem;
  font-weight: 600;
  color: var(--secondary-color);
  text-decoration: none;
  margin-bottom: 20px;
  display: inline-block;
  transition: color 0.3s ease;
  &:hover {
    color: var(--primary-color);
  }
`;

const MainLayout = styled.div`
  display: flex;
  gap: 40px;

  @media (max-width: 1024px) {
    flex-direction: column;
  }
`;

const PlayerContainer = styled.div`
  flex-grow: 1;
  position: relative;
`;

const AmbientGlow = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 0;
  border-radius: 16px;
  
  ${props => props.$isFallback ? css`
    background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
    filter: blur(60px);
    opacity: 0.4;
  ` : css`
    background-image: url(${props => props.$bgImage});
    background-size: cover;
    background-position: center;
    filter: blur(80px) saturate(150%) brightness(120%);
    opacity: 0.3;
  `}
`;

const PlayerWrapper = styled.div`
  position: relative;
  z-index: 1;
`;

const VideoPlayer = styled.div`
  width: 100%;
  aspect-ratio: 16 / 9;
  background-color: #000;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 10px 40px rgba(0,0,0,0.5);
  display: flex;
  align-items: center;
  justify-content: center;

  & > div {
    width: 100%;
    height: 100%;
  }

  iframe {
    width: 100%;
    height: 100%;
    border: none;
  }
`;

const LanguageOptions = styled.div`
  margin: 15px 0;
  padding: 15px;
  background: var(--glass-bg);
  border-radius: 10px;
  border: 1px solid var(--border-color);
`;

const LanguageTitle = styled.h3`
  font-size: 1rem;
  margin-bottom: 12px;
  color: var(--primary-color);
  display: flex;
  align-items: center;
  gap: 8px;
`;

const LanguageButtons = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
`;

const LanguageButton = styled.button`
  padding: 8px 16px;
  border-radius: 20px;
  border: 1px solid ${props => props.$selected ? 'var(--gradient-pink)' : 'var(--border-color)'};
  background: ${props => props.$selected ? 'rgba(255, 0, 127, 0.2)' : 'transparent'};
  color: ${props => props.$selected ? 'var(--gradient-pink)' : 'var(--secondary-color)'};
  font-size: 0.85rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 6px;

  &:hover {
    border-color: var(--gradient-pink);
    color: var(--gradient-pink);
  }
`;

const EpisodeInfo = styled.div`
  margin-top: 20px;
`;

const TitleWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
  gap: 20px;
`;

const EpisodeTitle = styled.h1`
  font-size: 1.8rem;
  flex-grow: 1;
`;

const EpisodeDescription = styled.div`
  margin: 15px 0;
  padding: 15px;
  background: var(--glass-bg);
  border-radius: 10px;
  border-left: 3px solid var(--gradient-pink);
`;

const DescriptionText = styled.p`
  color: var(--secondary-color);
  line-height: 1.6;
  font-size: 0.9rem;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 10px;
`;

const ActionButton = styled.button`
  background: var(--glass-bg);
  border: 1px solid var(--border-color);
  color: white;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 1rem;
  transition: all 0.3s ease;

  &:hover {
    border-color: var(--gradient-pink);
    transform: scale(1.1);
  }
`;

const NavigationButtons = styled.div`
  display: flex;
  gap: 10px;
`;

const NavButton = styled(Link)`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: var(--glass-bg);
  border: 1px solid var(--border-color);
  color: var(--primary-color);
  transition: all 0.3s ease;

  &.disabled {
    opacity: 0.3;
    pointer-events: none;
  }

  &:not(.disabled):hover {
    border-color: var(--gradient-pink);
    transform: scale(1.1);
  }
`;

const EpisodePlaylist = styled.div`
  width: 350px;
  flex-shrink: 0;
  height: 80vh;
  overflow-y: auto;
  
  h4 {
    font-size: 1.2rem;
    margin-bottom: 15px;
  }

  @media (max-width: 1024px) {
    width: 100%;
    height: auto;
    max-height: 60vh;
    margin-top: 40px;
  }
`;

const PlaylistItem = styled(Link)`
  display: flex;
  gap: 15px;
  padding: 10px;
  border-radius: 10px;
  margin-bottom: 10px;
  background-color: ${props => props.$isActive ? 'var(--glass-bg)' : 'transparent'};
  border: 1px solid ${props => props.$isActive ? 'var(--border-color)' : 'transparent'};
  transition: all 0.3s ease;
  position: relative;
  
  &:hover {
    background-color: var(--glass-bg);
  }

  img {
    width: 120px;
    height: 67.5px;
    object-fit: cover;
    border-radius: 6px;
    flex-shrink: 0;
  }

  div { 
    flex-grow: 1;
    position: relative;
  }
  h4 {
    font-size: 0.9rem;
    margin-bottom: 5px;
    color: ${props => props.$isActive ? 'var(--primary-color)' : 'var(--secondary-color)'};
    transition: color 0.3s ease;
  }
  p { font-size: 0.8rem; color: var(--secondary-color); }
`;

const DurationBadge = styled.span`
  position: absolute;
  top: 0;
  right: 0;
  color: var(--secondary-color);
  font-size: 0.8rem;
  font-weight: normal;
`;

const ErrorMessage = styled.div`
  text-align: center;
  padding: 40px;
  color: var(--secondary-color);
  
  a {
    color: var(--primary-color);
    text-decoration: none;
    display: inline-flex;
    align-items: center;
    gap: 8px;
    margin-top: 15px;
    
    &:hover {
      color: var(--gradient-pink);
    }
  }
`;

// Yorum Bileşenleri
const CommentsSection = styled.div`
  margin-top: 50px;
  padding-top: 30px;
  border-top: 1px solid var(--border-color);
`;

const CommentsTitle = styled.h2`
  font-size: 1.5rem; 
  margin-bottom: 20px;
  color: var(--primary-color);
`;

const CommentForm = styled.div`
  background: var(--glass-bg);
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 30px;
  border: 1px solid var(--border-color);
`;

const UserInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 15px;
`;

const UserAvatar = styled.img`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid var(--gradient-pink);
`;

const UserName = styled(Link)`
  font-weight: 600;
  color: var(--primary-color);
  text-decoration: none;
  font-size: 1rem;
  
  &:hover {
    color: var(--gradient-pink);
  }
`;

const CommentTextarea = styled.textarea`
  width: 100%;
  min-height: 100px;
  background: rgba(0, 0, 0, 0.3);
  border: 1px solid var(--border-color);
  border-radius: 8px;
  padding: 12px;
  color: var(--primary-color);
  font-size: 0.9rem;
  resize: vertical;
  
  &:focus {
    outline: none;
    border-color: var(--gradient-pink);
  }
`;

const FormActions = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 15px;
  gap: 15px;
`;

const SpoilerToggle = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  color: var(--secondary-color);
  font-size: 0.9rem;
  cursor: pointer;
`;

const SubmitButton = styled.button`
  padding: 10px 20px;
  background: var(--gradient-pink);
  border: none;
  border-radius: 8px;
  color: white;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover:not(:disabled) {
    transform: translateY(-2px);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const CommentsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const CommentItem = styled.div`
  background: var(--glass-bg);
  border-radius: 12px;
  padding: 20px;
  border: 1px solid var(--border-color);
`;

const CommentHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 12px;
`;

const CommentUser = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const CommentAvatar = styled.img`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  object-fit: cover;
`;

const CommentUserInfo = styled.div`
  display: flex;
  flex-direction: column;
`;

const CommentUsername = styled(Link)`
  font-weight: 600;
  color: var(--primary-color);
  text-decoration: none;
  font-size: 0.95rem;
  
  &:hover {
    color: var(--gradient-pink);
  }
`;

const CommentDate = styled.span`
  font-size: 0.8rem;
  color: var(--secondary-color);
  margin-top: 2px;
`;

const CommentActions = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
`;

const SpoilerBadge = styled.span`
  background: rgba(148, 0, 211, 0.2);
  color: #9400d3;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 0.7rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 4px;
`;

const DeleteButton = styled.button`
  background: rgba(220, 53, 69, 0.2);
  border: 1px solid rgba(220, 53, 69, 0.5);
  color: #dc3545;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(220, 53, 69, 0.3);
  }
`;

const SpoilerContent = styled.div`
  background: rgba(148, 0, 211, 0.1);
  border: 1px solid rgba(148, 0, 211, 0.3);
  border-radius: 8px;
  padding: 16px;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;

  &:hover {
    background: rgba(148, 0, 211, 0.15);
  }
`;

const SpoilerWarning = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  color: #9400d3;
  font-weight: 600;
  text-align: center;
`;

const RevealedContent = styled.div`
  color: var(--primary-color);
  line-height: 1.6;
  font-size: 0.9rem;
`;

const ShowSpoilerButton = styled.button`
  background: rgba(148, 0, 211, 0.2);
  border: 1px solid rgba(148, 0, 211, 0.5);
  color: #9400d3;
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.3s ease;
  margin-top: 10px;

  &:hover {
    background: rgba(148, 0, 211, 0.3);
    transform: translateY(-1px);
  }
`;

const NoComments = styled.div`
  text-align: center;
  padding: 40px;
  color: var(--secondary-color);
`;

const LoginPrompt = styled.div`
  text-align: center;
  padding: 30px;
  background: var(--glass-bg);
  border-radius: 12px;
  border: 1px solid var(--border-color);
`;

const LoginButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  background: var(--gradient-pink);
  color: white;
  text-decoration: none;
  border-radius: 8px;
  font-weight: 600;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
  }
`;

// --- WatchPage Component ---

const WatchPage = () => {
  const { id, seasonNumber, episodeNumber, mediaType } = useParams();
  const navigate = useNavigate();
  const [contentDetails, setContentDetails] = useState(null);
  const [alternateTitles, setAlternateTitles] = useState([]);
  const [adSettings, setAdSettings] = useState({
    ad_watch_top: '',
    ad_watch_bottom: ''
  });
  const [episodeDetails, setEpisodeDetails] = useState(null);
  const [seasonEpisodes, setSeasonEpisodes] = useState([]);
  const [iframeHtml, setIframeHtml] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Yeni state'ler
  const [selectedLanguage, setSelectedLanguage] = useState(null);
  const [availableLanguages, setAvailableLanguages] = useState([]);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [isSpoiler, setIsSpoiler] = useState(false);
  const [commentLoading, setCommentLoading] = useState(false);
  const [userData, setUserData] = useState(null);
  const [isFavorite, setIsFavorite] = useState(false);
  const [isWatchlisted, setIsWatchlisted] = useState(false);
  const [revealedSpoilers, setRevealedSpoilers] = useState(new Set());

  const currentSeasonNum = seasonNumber ? parseInt(seasonNumber) : null;
  const currentEpisodeNum = episodeNumber ? parseInt(episodeNumber) : null;

  // Spoiler göster/gizle
  const toggleSpoiler = (commentId) => {
    setRevealedSpoilers(prev => {
      const newSet = new Set(prev);
      if (newSet.has(commentId)) {
        newSet.delete(commentId);
      } else {
        newSet.add(commentId);
      }
      return newSet;
    });
  };

  // Kullanıcı verilerini çek
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const response = await fetch(getApiUrl(API_ENDPOINTS.CHECK_AUTH), {
          credentials: 'include'
        });
        const data = await response.json();
        if (data.success) {
          setUserData(data.user);
          
          // Favori ve watchlist durumunu kontrol et
          if (contentDetails) {
            checkUserLists(data.user.id, contentDetails.id);
          }
        }
      } catch (error) {
        console.error('Kullanıcı verisi çekilemedi:', error);
      }
    };

    fetchUserData();
  }, [contentDetails]);

  // Favori ve watchlist kontrolü - DÜZELTİLDİ
  const checkUserLists = async (userId, contentId) => {
    try {
      // Favori kontrolü
      const favResponse = await fetch(`${getApiUrl(API_ENDPOINTS.CHECK_FAVORITE)}?content_id=${contentId}`, {
        credentials: 'include'
      });
      if (favResponse.ok) {
        const favData = await favResponse.json();
        setIsFavorite(favData.isFavorite);
      }

      // Watchlist kontrolü - DÜZELTİLDİ
      const watchResponse = await fetch(`${getApiUrl(API_ENDPOINTS.CHECK_WATCHLIST)}?content_id=${contentId}&season=${currentSeasonNum}&episode=${currentEpisodeNum}`, {
        credentials: 'include'
      });
      if (watchResponse.ok) {
        const watchData = await watchResponse.json();
        setIsWatchlisted(watchData.isWatchlisted);
      }
    } catch (error) {
      console.error('Liste kontrol hatası:', error);
    }
  };

  // Yorumları getir
  const fetchComments = async (contentId) => {
    try {
      const response = await fetch(`${getApiUrl(API_ENDPOINTS.COMMENTS)}?content_id=${contentId}`);
      const data = await response.json();
      if (data.success) {
        setComments(data.comments);
      }
    } catch (error) {
      console.error('Yorumlar getirilemedi:', error);
    }
  };

  // Yorum ekle
  const handleAddComment = async () => {
    if (!newComment.trim() || !userData || !contentDetails) return;

    setCommentLoading(true);
    try {
      const response = await fetch(getApiUrl(API_ENDPOINTS.COMMENTS), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          content_id: contentDetails.id,
          comment_text: newComment,
          is_spoiler: isSpoiler
        }),
        credentials: 'include'
      });

      const data = await response.json();
      if (data.success) {
        setNewComment('');
        setIsSpoiler(false);
        setComments(prev => [data.comment, ...prev]);
      } else {
        alert(data.message);
      }
    } catch (error) {
      console.error('Yorum eklenemedi:', error);
      alert('Yorum eklenirken hata oluştu');
    } finally {
      setCommentLoading(false);
    }
  };

  // Yorum sil
  const handleDeleteComment = async (commentId) => {
    if (!window.confirm('Yorumu silmek istediğinizden emin misiniz?')) return;

    try {
      const response = await fetch(getApiUrl(API_ENDPOINTS.DELETE_COMMENTS), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ comment_id: commentId }),
        credentials: 'include'
      });

      const data = await response.json();
      if (data.success) {
        setComments(prev => prev.filter(comment => comment.id !== commentId));
      } else {
        alert(data.message);
      }
    } catch (error) {
      console.error('Yorum silinemedi:', error);
      alert('Yorum silinirken hata oluştu');
    }
  };

  // Favori işlemi
  const toggleFavorite = async () => {
    if (!userData) {
      alert('Favorilere eklemek için giriş yapın!');
      navigate('/login');
      return;
    }

    try {
      const response = await fetch(getApiUrl(API_ENDPOINTS.TOGGLE_FAVORITE), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          content_id: contentDetails.id,
          media_type: contentDetails.media_type
        }),
        credentials: 'include'
      });

      const data = await response.json();
      if (data.success) {
        setIsFavorite(data.isFavorite);
      } else {
        alert(data.message);
      }
    } catch (error) {
      console.error("Favori işlemi sırasında hata:", error);
      alert('Favori işlemi sırasında hata oluştu');
    }
  };

  // Watchlist işlemi - DÜZELTİLDİ
  const toggleWatchlist = async () => {
    if (!userData) {
      alert('Watchlist için giriş yapın!');
      navigate('/login');
      return;
    }

    try {
      const response = await fetch(getApiUrl(API_ENDPOINTS.TOGGLE_WATCHLIST), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          content_id: contentDetails.id,
          media_type: contentDetails.media_type,
          season_number: currentSeasonNum,
          episode_number: currentEpisodeNum
        }),
        credentials: 'include'
      });

      const data = await response.json();
      if (data.success) {
        setIsWatchlisted(data.isWatchlisted);
      } else {
        alert(data.message);
      }
    } catch (error) {
      console.error("Watchlist işlemi sırasında hata:", error);
      alert('Watchlist işlemi sırasında hata oluştu');
    }
  };

// İçerik detaylarını yükle - GÜNCELLENMİŞ
useEffect(() => {
  const loadData = async () => {
    setLoading(true);
    setError(null);
    
    // Bölüm değiştiğinde iframe'i temizle
    setIframeHtml('');
    setSelectedLanguage(null);
    setAvailableLanguages([]);
    
    try {
      // İçerik detaylarını çek
      const response = await fetch(`${getApiUrl(API_ENDPOINTS.GET_CONTENT)}?tmdb_id=${id}`);
      
      if (!response.ok) {
        throw new Error('İçerik bulunamadı');
      }

      const contentData = await response.json();
      
      if (contentData.error || !contentData.id) {
        throw new Error('Bu içerik sitede bulunmuyor');
      }

      // Reklam ayarlarını çek
      try {
        const adResponse = await fetch(getApiUrl(API_ENDPOINTS.GET_PUBLIC_SETTINGS));
        if (adResponse.ok) {
          const adData = await adResponse.json();
          setAdSettings({
            ad_watch_top: adData.ad_watch_top || '',
            ad_watch_bottom: adData.ad_watch_bottom || ''
          });
        }
      } catch (adError) {
        console.error('Reklam ayarları yüklenirken hata:', adError);
      }

      setContentDetails(contentData);

      // Alternatif başlıkları çek
      try {
        const titlesResponse = await fetch(`${getApiUrl(API_ENDPOINTS.GET_ALTERNATE_TITLES)}?content_id=${contentData.id}`);
        if (titlesResponse.ok) {
          const titlesData = await titlesResponse.json();
          // API'den gelen veriyi normalize et - string array'e çevir
          let titles = [];
          if (Array.isArray(titlesData)) {
            titles = titlesData.map(t => {
              // Eğer object ise title property'sini al, değilse direkt kullan
              if (typeof t === 'string') return t;
              if (t && typeof t === 'object') {
                return t.title || t.name || t.alternate_title || String(t);
              }
              return String(t);
            }).filter(t => t && typeof t === 'string' && t.trim() !== '');
          }
          setAlternateTitles(titles);
        }
      } catch (titlesError) {
        console.error('Alternatif başlıklar çekilirken hata:', titlesError);
      }

      // Yorumları getir
      fetchComments(contentData.id);

      // Dizi/anime ise bölüm bilgilerini getir
      if (mediaType === 'tv' || mediaType === 'anime') {
        // Önce sezonları çek
        const seasonsResponse = await fetch(`${getApiUrl(API_ENDPOINTS.GET_SEASONS)}?content_id=${contentData.id}`);
        const seasons = await seasonsResponse.json();
        
        
        if (Array.isArray(seasons) && seasons.length > 0) {
          // Mevcut sezonu bul
          const currentSeason = seasons.find(s => s.season_number == seasonNumber);
          
          if (currentSeason) {
            // Bölümleri çek
            const episodesResponse = await fetch(`${getApiUrl(API_ENDPOINTS.GET_EPISODES)}?season_id=${currentSeason.id}`);
            const episodes = await episodesResponse.json();
            
            
            if (Array.isArray(episodes)) {
              setSeasonEpisodes(episodes);
              
              // Mevcut bölümü bul
              const currentEp = episodes.find(ep => ep.episode_number == episodeNumber);
              
              // Bölüm sitede yüklü değilse hata göster
              if (!currentEp) {
                throw new Error('Bu bölüm sitede henüz yüklenmemiş');
              }
              
              setEpisodeDetails(currentEp);
              
              // Dil seçeneklerini ayarla - GÜNCELLENMİŞ
              const languages = [];
              
              // TR Dublaj
              if (currentEp?.tr_dublaj) {
                languages.push({
                  type: 'dublaj',
                  label: 'TR Dublaj',
                  iframe: currentEp.tr_dublaj,
                  icon: <FaMicrophone />
                });
              }
              
              // TR Altyazı
              if (currentEp?.tr_altyazi) {
                languages.push({
                  type: 'altyazi',
                  label: 'TR Altyazı',
                  iframe: currentEp.tr_altyazi,
                  icon: <FaClosedCaptioning />
                });
              }
              
              // Fansub seçenekleri
              if (currentEp?.fansubs && currentEp.fansubs !== '{}') {
                try {
                  const fansubs = JSON.parse(currentEp.fansubs);
                  Object.entries(fansubs).forEach(([fansubName, iframeHtml]) => {
                    languages.push({
                      type: 'fansub',
                      label: fansubName,
                      iframe: iframeHtml,
                      icon: <FaClosedCaptioning />
                    });
                  });
                } catch (e) {
                  console.error('Fansub parse hatası:', e);
                }
              }
              
              // Orijinal iframe (varsayılan)
              if (currentEp?.iframe_code) {
                languages.push({
                  type: 'orijinal',
                  label: 'Orijinal',
                  iframe: currentEp.iframe_code,
                  icon: <FaClosedCaptioning />
                });
              }
              
              setAvailableLanguages(languages);
              
              if (languages.length > 0) {
                setSelectedLanguage(languages[0]);
                setIframeHtml(languages[0].iframe);
              }
            }
          }
        }
      }

      // MediaType URL'den gelmiyorsa contentData'dan al
      const finalMediaType = mediaType || contentData.media_type;
      

      // Film ise dil seçeneklerini ayarla
      if (finalMediaType === 'movie') {
        const languages = [];
        
        // TR Dublaj
        if (contentData.tr_dublaj && typeof contentData.tr_dublaj === 'string' && contentData.tr_dublaj.trim() !== '') {
          languages.push({
            type: 'dublaj',
            label: 'TR Dublaj',
            iframe: contentData.tr_dublaj,
            icon: <FaMicrophone />
          });
        }
        
        // TR Altyazı
        if (contentData.tr_altyazi && typeof contentData.tr_altyazi === 'string' && contentData.tr_altyazi.trim() !== '') {
          languages.push({
            type: 'altyazi',
            label: 'TR Altyazı',
            iframe: contentData.tr_altyazi,
            icon: <FaClosedCaptioning />
          });
        }
        
        // Orijinal iframe
        if (contentData.iframe_code && typeof contentData.iframe_code === 'string' && contentData.iframe_code.trim() !== '') {
          languages.push({
            type: 'orijinal',
            label: 'Orijinal',
            iframe: contentData.iframe_code,
            icon: <FaClosedCaptioning />
          });
        }
        
        setAvailableLanguages(languages);
        if (languages.length > 0) {
          setSelectedLanguage(languages[0]);
          setIframeHtml(languages[0].iframe);
        }
      }

    } catch (error) {
      console.error("Veriler çekilirken hata oluştu:", error);
      setError('İçerik yüklenirken hata oluştu');
    } finally {
      setLoading(false);
      window.scrollTo(0, 0);
    }
  };
  
  loadData();
}, [id, seasonNumber, episodeNumber, mediaType]);

  // Yükleniyor durumu
  if (loading) {
    return (
      <WatchPageContainer initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
        <Header />
        <div style={{ textAlign: 'center', padding: '50px', color: 'var(--secondary-color)' }}>
          Yükleniyor...
        </div>
      </WatchPageContainer>
    );
  }

  // Hata durumu
  if (error || !contentDetails) {
    return (
      <WatchPageContainer initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
        <Header />
        <ErrorMessage>
          <h3>{error || 'İçerik bulunamadı'}</h3>
          <Link to="/">
            <FaHome /> Ana Sayfaya Dön
          </Link>
        </ErrorMessage>
      </WatchPageContainer>
    );
  }

  const finalTitle = contentDetails.title;
  const episodeTitleText = (mediaType === 'tv' || mediaType === 'anime') 
    ? `S${seasonNumber} E${episodeNumber}: ${episodeDetails?.episode_title || contentDetails.title}` 
    : finalTitle;
  
  const ambientImagePath = contentDetails.backdrop_path;
  const useFallbackGlow = !ambientImagePath || !iframeHtml;

  const getImageUrl = (path, size = 'w500') => {
    // Path kontrolü - boş, null, undefined veya sadece whitespace ise data URI döndür
    if (!path || path === 'null' || path === 'undefined' || (typeof path === 'string' && path.trim() === '')) {
      // Path yoksa direkt data URI döndür (placeholder spam'ı önlemek için)
      return 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgZmlsbD0iIzExMSIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTIiIGZpbGw9IiM2NjYiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj5SZXNpbSBZb2s8L3RleHQ+PC9zdmc+';
    }
    
    // String'e çevir ve trim yap
    path = String(path).trim();
    
    if (path.includes('/proxy-image.php')) return path;
    if (path.startsWith('data:')) return path; // Zaten data URI ise direkt döndür
    
    // Eğer path tam bir TMDB URL'si ise, sadece path kısmını çıkar
    if (path.includes('image.tmdb.org')) {
      const match = path.match(/\/t\/p\/[^\/]+\/(.+)$/);
      if (match) {
        path = '/' + match[1];
      }
    }
    
    // Path'i normalize et - başında / olmalı (http/https ile başlamıyorsa)
    let normalizedPath = path;
    if (!normalizedPath.startsWith('/') && !normalizedPath.startsWith('http')) {
      normalizedPath = '/' + normalizedPath;
    }

    // Tek bir proxy yolu kullanıyoruz
    return `${getApiUrl(API_ENDPOINTS.PROXY_IMAGE)}?path=${encodeURIComponent(normalizedPath)}&size=${size}`;
  };

  // Navigasyon URL'leri
  const getNavUrl = (episode) => {
    if (!episode) return '#';
    return `/watch/tv/${id}/season/${episode.season}/episode/${episode.episode}`;
  };

  const getPlaylistUrl = (ep) => {
    return `/watch/tv/${id}/season/${seasonNumber}/episode/${ep.episode_number}`;
  };

  const getDetailUrl = () => {
    // Anime ise anime olarak yönlendir, tv değil
    if (contentDetails && contentDetails.media_type === 'anime') {
      return `/anime/${id}`;
    }
    return `/${mediaType}/${id}`;
  };

  // SEO için içerik bilgilerini hazırla
  // Eğer episode'da SEO alanları varsa onları kullan, yoksa otomatik oluştur
  const seoTitle = (mediaType === 'tv' || mediaType === 'anime') && episodeDetails
    ? (episodeDetails.seo_title || `${contentDetails.title} Sezon ${seasonNumber} Bölüm ${episodeNumber} Türkçe İzle`)
    : `${contentDetails.title} Türkçe İzle`;
  
  const seoDescription = (mediaType === 'tv' || mediaType === 'anime') && episodeDetails
    ? (episodeDetails.seo_description || `${contentDetails.title} Sezon ${seasonNumber} Bölüm ${episodeNumber} Türkçe dublaj ve altyazılı olarak izleyin. ${episodeDetails.episode_title || ''} ${episodeDetails.episode_overview ? episodeDetails.episode_overview.substring(0, 120) : ''} Ücretsiz ve HD kalitede.`)
    : `${contentDetails.title} Türkçe dublaj ve altyazılı olarak izleyin. ${contentDetails.overview ? contentDetails.overview.substring(0, 150) : ''} Ücretsiz ve HD kalitede.`;
  
  // SEO Keywords - alternatif başlıklarla birlikte
  const seoKeywords = (() => {
    if (!contentDetails) return 'film izle, dizi izle, türkçe dublaj, türkçe altyazı';
    
    // Episode SEO keywords varsa onu kullan (zaten alternatif başlıklar dahil)
    if ((mediaType === 'tv' || mediaType === 'anime') && episodeDetails && episodeDetails.seo_keywords) {
      return episodeDetails.seo_keywords;
    }
    
    // Episode SEO keywords yoksa otomatik oluştur (alternatif başlıklarla)
    // String kontrolü ekle - sadece string olanları al
    const allTitles = [
      contentDetails.title,
      ...alternateTitles
    ].filter(t => {
      // String kontrolü ve boş olmayan kontrolü
      return typeof t === 'string' && t.trim() !== '';
    });
    const keywordsParts = [];
    
    if ((mediaType === 'tv' || mediaType === 'anime') && episodeDetails) {
      // Episode için
      allTitles.forEach(title => {
        keywordsParts.push(`${title} izle`);
        keywordsParts.push(`${title} sezon ${seasonNumber} bölüm ${episodeNumber} izle`);
        keywordsParts.push(`${title} türkçe izle`);
        keywordsParts.push(`${title} sezon ${seasonNumber} bölüm ${episodeNumber} türkçe izle`);
        keywordsParts.push(`${title} türkçe dublaj`);
        keywordsParts.push(`${title} türkçe altyazı`);
      });
    } else {
      // Film için
      allTitles.forEach(title => {
        keywordsParts.push(`${title} izle`);
        keywordsParts.push(`${title} türkçe izle`);
        keywordsParts.push(`${title} türkçe dublaj`);
        keywordsParts.push(`${title} türkçe altyazı`);
      });
    }
    
    keywordsParts.push(contentDetails.media_type === 'movie' ? 'film izle' : 'dizi izle');
    keywordsParts.push('türkçe film izle');
    keywordsParts.push('türkçe dizi izle');
    
    return keywordsParts.join(', ');
  })();
  
  const seoImage = episodeDetails?.thumbnail_path 
    ? getImageUrlHelper(episodeDetails.thumbnail_path, 'original')
    : contentDetails?.backdrop_path
    ? getImageUrlHelper(contentDetails.backdrop_path, 'original')
    : contentDetails?.poster_path
    ? getImageUrlHelper(contentDetails.poster_path, 'original')
    : '';
  
  const seoCanonical = (mediaType === 'tv' || mediaType === 'anime') && episodeDetails && episodeDetails.seo_canonical_url
    ? episodeDetails.seo_canonical_url
    : window.location.href;

  return (
    <WatchPageContainer initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
      <SEO 
        title={seoTitle}
        description={seoDescription}
        keywords={seoKeywords}
        image={seoImage}
        url={seoCanonical}
        type={mediaType === 'movie' ? 'video.movie' : 'video.episode'}
      />
      <Header />
      
      {/* Reklam - Üst (İçerik adının üstünde) */}
      {adSettings.ad_watch_top && (
        <div style={{ padding: '0 0 20px 0', width: '100%', overflow: 'visible' }}>
          <AdBanner 
            adCode={adSettings.ad_watch_top}
            height="120px"
            mobileHeight="100px"
            margin="0"
            mobileMargin="0"
            showLabel={false}
            bgColor="transparent"
            border="none"
            showBox={false}
            overflow="visible"
          />
        </div>
      )}

      <ShowTitleLink to={getDetailUrl()}>
        {finalTitle}
        {(mediaType === 'tv' || mediaType === 'anime') && ` - Sezon ${seasonNumber} Bölüm ${episodeNumber}`}
      </ShowTitleLink>
      
      <MainLayout>
        <PlayerContainer>
          <AmbientGlow 
            $bgImage={ambientImagePath ? `https://image.tmdb.org/t/p/original${ambientImagePath}` : ''}
            $isFallback={useFallbackGlow}
          />
          <PlayerWrapper>
            <VideoPlayer>
              {iframeHtml ? (
                <div dangerouslySetInnerHTML={{ __html: iframeHtml }} />
              ) : (
                <div style={{ color: 'white', textAlign: 'center', padding: '40px' }}>
                  <p>İçerik henüz eklenmedi</p>
                </div>
              )}
            </VideoPlayer>

            {/* Dil Seçenekleri - Sadece TR dublaj/altyazı/fansub varsa göster */}
            {availableLanguages.length > 1 || (availableLanguages.length === 1 && availableLanguages[0].type !== 'orijinal') ? (
              <LanguageOptions>
                <LanguageTitle>
                  <FaClosedCaptioning />
                  Dil Seçenekleri
                </LanguageTitle>
                <LanguageButtons>
                  {availableLanguages
                    .filter(lang => lang.iframe && lang.iframe.trim() !== '')
                    .map((language, index) => (
                      <LanguageButton
                        key={index}
                        $selected={selectedLanguage?.label === language.label}
                        onClick={() => {
                          setSelectedLanguage(language);
                          setIframeHtml(language.iframe);
                        }}
                      >
                        {language.icon}
                        {language.label}
                      </LanguageButton>
                    ))}
                </LanguageButtons>
              </LanguageOptions>
            ) : null}

            {/* Reklam - Video Altı */}
            {adSettings.ad_watch_bottom && (
              <div style={{ marginTop: '30px', marginBottom: '30px', width: '100%', overflow: 'visible' }}>
                <AdBanner 
                  adCode={adSettings.ad_watch_bottom}
                  height="250px"
                  mobileHeight="200px"
                  margin="0"
                  mobileMargin="0"
                  showLabel={false}
                  bgColor="transparent"
                  border="none"
                  showBox={false}
                  overflow="visible"
                />
              </div>
            )}

            <EpisodeInfo>
              <TitleWrapper>
                <EpisodeTitle>{episodeTitleText}</EpisodeTitle>
                <ActionButtons>
                  <ActionButton onClick={toggleWatchlist} title="Sonra İzle">
                    {isWatchlisted ? <FaBookmark color="var(--primary-color)" /> : <FaRegBookmark />}
                  </ActionButton>
                </ActionButtons>
              </TitleWrapper>
              
              {episodeDetails?.episode_overview && (
                <EpisodeDescription>
                  <DescriptionText>{episodeDetails.episode_overview}</DescriptionText>
                </EpisodeDescription>
              )}
            </EpisodeInfo>
          </PlayerWrapper>

          {/* Reklam - Videonun Altında */}
          {adSettings.ad_watch_bottom && (
            <AdBanner 
              adCode={adSettings.ad_watch_bottom}
              height="250px"
              mobileHeight="200px"
              margin="30px 0 0 0"
              mobileMargin="20px 0 0 0"
              showLabel={false}
              bgColor="transparent"
              border="none"
            />
          )}
        </PlayerContainer>

        {/* Bölüm Listesi - SAĞ TARAFTA */}
            {(mediaType === 'tv' || mediaType === 'anime') && seasonEpisodes.length > 0 && (
              <EpisodePlaylist>
                <h4>Sezon {seasonNumber} ({seasonEpisodes.length} Bölüm)</h4>
                {seasonEpisodes.map(ep => (
                  <PlaylistItem 
                    key={ep.id}
                    to={getPlaylistUrl(ep)}
                    $isActive={ep.episode_number == currentEpisodeNum}
                  >
                    <img 
                      src={(() => {
                        // Önce thumbnail_path'i kontrol et, boş değilse kullan
                        const thumbnailPath = ep.thumbnail_path && ep.thumbnail_path.trim() !== '' ? ep.thumbnail_path : null;
                        const imagePath = thumbnailPath || contentDetails?.backdrop_path || contentDetails?.poster_path || null;
                        // imagePath null ise getImageUrl data URI döndürecek
                        return getImageUrl(imagePath, 'w300');
                      })()}
                      alt={ep.episode_title} 
                      onError={(e) => {
                        // Eğer zaten data URI ise, tekrar deneme (sonsuz döngüyü önle)
                        if (e.target.src.startsWith('data:')) {
                          e.target.onerror = null;
                          return;
                        }
                        // Eğer zaten placeholder'a düştüyse, data URI'ye geç
                        if (e.target.src.includes('placeholder-image.jpg')) {
                          e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgZmlsbD0iIzExMSIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTIiIGZpbGw9IiM2NjYiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj5SZXNpbSBZb2s8L3RleHQ+PC9zdmc+';
                          e.target.onerror = null; // Sonsuz döngüyü önle
                          return;
                        }
                        // İlk hata: fallback path'leri dene
                        const fallbackPath = contentDetails.backdrop_path || contentDetails.poster_path;
                        if (fallbackPath && fallbackPath !== ep.thumbnail_path) {
                          e.target.src = getImageUrl(fallbackPath, 'w300');
                          // Fallback de çalışmazsa data URI'ye geç
                          e.target.onerror = () => {
                            e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgZmlsbD0iIzExMSIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTIiIGZpbGw9IiM2NjYiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj5SZXNpbSBZb2s8L3RleHQ+PC9zdmc+';
                            e.target.onerror = null;
                          };
                        } else {
                          // Fallback yoksa direkt data URI'ye geç
                          e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgZmlsbD0iIzExMSIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTIiIGZpbGw9IiM2NjYiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj5SZXNpbSBZb2s8L3RleHQ+PC9zdmc+';
                          e.target.onerror = null;
                        }
                      }}
                    />
                    <div>
                      <h4>{ep.episode_number}. {ep.episode_title || `Bölüm ${ep.episode_number}`}</h4>
                      <p>{ep.air_date ? new Date(ep.air_date).toLocaleDateString('tr-TR') : ''}</p>
                      {ep.duration && (
                        <DurationBadge>{ep.duration} dk</DurationBadge>
                      )}
                    </div>
                  </PlaylistItem>
                ))}
              </EpisodePlaylist>
            )}
      </MainLayout>

      {/* Yorumlar Bölümü - AŞAĞIDA */}
      <CommentsSection>
        <CommentsTitle>Yorumlar</CommentsTitle>
        
        {userData ? (
          <CommentForm>
            <UserInfo>
              <UserAvatar 
                src={userData.avatar_url || '/default-avatar.png'} 
                alt={userData.username}
                onError={(e) => {
                  e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48Y2lyY2xlIGN4PSIyMCIgY3k9IjIwIiByPSIyMCIgZmlsbD0iIzMzMyIvPjx0ZXh0IHg9IjIwIiB5PSIyNCIgZm9udC1mYW1pbHk9IkFyaWFsLCBzYW5zLXNlcmlmIiBmb250LXNpemU9IjE0IiBmaWxsPSIjNjY2IiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBkb21pbmFudC1iYXNlbGluZT0ibWlkZGxlIj7OuzwvdGV4dD48L3N2Zz4=';
                }}
              />
              <UserName to={`/profil/${userData.username}`}>
                {userData.username}
              </UserName>
            </UserInfo>
            <CommentTextarea
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              placeholder="Yorumunuzu buraya yazın..."
              maxLength={1000}
            />
            <FormActions>
              <SpoilerToggle>
                <input
                  type="checkbox"
                  checked={isSpoiler}
                  onChange={(e) => setIsSpoiler(e.target.checked)}
                />
                <FaExclamationTriangle />
                Spoiler İçerir
              </SpoilerToggle>
              <SubmitButton
                onClick={handleAddComment}
                disabled={!newComment.trim() || commentLoading}
              >
                {commentLoading ? 'Gönderiliyor...' : 'Yorum Yap'}
              </SubmitButton>
            </FormActions>
          </CommentForm>
        ) : (
          <LoginPrompt>
            <p>Yorum yapmak için giriş yapmalısınız</p>
            <LoginButton to="/login">
              <FaUser />
              Giriş Yap
            </LoginButton>
          </LoginPrompt>
        )}

        <CommentsList>
          {comments.length > 0 ? (
            comments.map((comment) => (
              <CommentItem key={comment.id}>
                <CommentHeader>
                  <CommentUser>
                    <CommentAvatar 
                      src={comment.avatar_url || '/default-avatar.png'}
                      alt={comment.username}
                      onError={(e) => {
                        e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzYiIGhlaWdodD0iMzYiIHZpZXdCb3g9IjAgMCAzNiAzNiIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48Y2lyY2xlIGN4PSIxOCIgY3k9IjE4IiByPSIxOCIgZmlsbD0iIzMzMyIvPjx0ZXh0IHg9IjE4IiB5PSIyMiIgZm9udC1mYW1pbHk9IkFyaWFsLCBzYW5zLXNlcmlmIiBmb250LXNpemU9IjEyIiBmaWxsPSIjNjY2IiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBkb21pbmFudC1iYXNlbGluZT0ibWlkZGxlIj7OuzwvdGV4dD48L3N2Zz4=';
                      }}
                    />
                    <CommentUserInfo>
                      <CommentUsername to={`/profil/${comment.username}`}>
                        {comment.username}
                      </CommentUsername>
                      <CommentDate>
                        {new Date(comment.created_at).toLocaleDateString('tr-TR', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </CommentDate>
                    </CommentUserInfo>
                  </CommentUser>
                  <CommentActions>
                    {comment.is_spoiler && (
                      <SpoilerBadge>
                        <FaExclamationTriangle />
                        SPOILER
                      </SpoilerBadge>
                    )}
                    {userData && userData.id === comment.user_id && (
                      <DeleteButton
                        onClick={() => handleDeleteComment(comment.id)}
                        title="Yorumu Sil"
                      >
                        <FaTrash size={12} />
                      </DeleteButton>
                    )}
                  </CommentActions>
                </CommentHeader>
                
                {comment.is_spoiler && !revealedSpoilers.has(comment.id) ? (
                  <SpoilerContent onClick={() => toggleSpoiler(comment.id)}>
                    <SpoilerWarning>
                      <FaExclamationTriangle />
                      Bu yorum spoiler içeriyor
                    </SpoilerWarning>
                    <ShowSpoilerButton onClick={(e) => {
                      e.stopPropagation();
                      toggleSpoiler(comment.id);
                    }}>
                      <FaEye />
                      Spoiler'ı Göster
                    </ShowSpoilerButton>
                  </SpoilerContent>
                ) : (
                  <RevealedContent>
                    {comment.comment_text}
                    {comment.is_spoiler && revealedSpoilers.has(comment.id) && (
                      <ShowSpoilerButton onClick={() => toggleSpoiler(comment.id)}>
                        <FaEyeSlash />
                        Spoiler'ı Gizle
                      </ShowSpoilerButton>
                    )}
                  </RevealedContent>
                )}
              </CommentItem>
            ))
          ) : (
            <NoComments>
              <p>Henüz yorum yapılmamış.</p>
              <p>İlk yorumu siz yapın!</p>
            </NoComments>
          )}
        </CommentsList>
      </CommentsSection>
    </WatchPageContainer>
  );
};

export default WatchPage;
